import React from "react";
import TwoDFemale from "../assets/2D_Female_Avatar.png";
import TwoDMale from "../assets/2D_Male_Avatar.png";

// 2D Avatar: large circular presentation with a premium speaking aura.
// The avatar image itself stays static; only the surrounding glow/rings animate.
const TwoDAvatar = ({ gender = "male", isAISpeaking = false }) => {
  const src = gender === "female" ? TwoDFemale : TwoDMale;

  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
      aria-hidden={true}
    >
      <div
        className={`relative flex items-center justify-center avatar-2d-wrapper ${
          isAISpeaking ? "speaking" : "listening"
        }`}
      >
        {/* Soft speaking halo */}
        <div className="avatar-2d-ambient" />

        {/* Layered aura behind the avatar */}
        <div className="avatar-2d-aura avatar-2d-aura-one" />
        <div className="avatar-2d-aura avatar-2d-aura-two" />

        {/* Animated speaking rings */}
        <div className="avatar-2d-speaking-ring avatar-2d-speaking-ring-one" />
        <div className="avatar-2d-speaking-ring avatar-2d-speaking-ring-two" />

        {/* Large circular avatar */}
        <div className="avatar-2d-frame">
          <div className="avatar-2d-image-wrap">
            <img src={src} alt="2D Avatar" className="avatar-2d-image" />
          </div>
        </div>

        {/* Subtle ring that remains visible in listening mode */}
        <div className="avatar-2d-ring" />

        <style>{`
          .avatar-2d-wrapper {
            position: relative;
            width: 100%;
            height: 100%;
            isolation: isolate;
          }

          /* Large responsive circular avatar */
          .avatar-2d-frame {
            position: relative;
            z-index: 30;
            width: clamp(270px, 38vh, 420px);
            height: clamp(270px, 38vh, 420px);
            max-width: 72%;
            max-height: 78%;
            flex-shrink: 0;
            padding: 5px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            background: linear-gradient(
              145deg,
              rgba(255, 255, 255, 0.92),
              rgba(196, 181, 253, 0.65) 42%,
              rgba(124, 58, 237, 0.55) 100%
            );
            box-shadow:
              0 12px 38px rgba(0, 0, 0, 0.48),
              0 0 0 1px rgba(255, 255, 255, 0.18);
            transition:
              box-shadow 500ms ease,
              transform 500ms ease;
          }

          .avatar-2d-image-wrap {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #050507;
          }

          .avatar-2d-image {
            width: 100%;
            height: 100%;
            display: block;
            object-fit: cover;
            object-position: center;
            border-radius: 50%;
            transform: translateZ(0);
            backface-visibility: hidden;
          }

          /* Static listening ring */
          .avatar-2d-ring {
            position: absolute;
            z-index: 28;
            width: clamp(286px, 40vh, 438px);
            height: clamp(286px, 40vh, 438px);
            max-width: 76%;
            max-height: 82%;
            border-radius: 50%;
            border: 1px solid rgba(196, 181, 253, 0.22);
            box-shadow:
              0 0 0 1px rgba(124, 58, 237, 0.08),
              inset 0 0 28px rgba(124, 58, 237, 0.06);
            opacity: 1;
            transform: scale(1);
            pointer-events: none;
            transition:
              opacity 450ms ease,
              transform 450ms ease,
              box-shadow 450ms ease;
          }

          /* Speaking glow remains behind the avatar */
          .avatar-2d-ambient {
            position: absolute;
            z-index: 4;
            width: clamp(330px, 50vh, 540px);
            height: clamp(330px, 50vh, 540px);
            max-width: 92%;
            max-height: 94%;
            border-radius: 50%;
            pointer-events: none;
            opacity: 0;
            transform: scale(0.82);
            filter: blur(34px);
            background: radial-gradient(
              circle,
              rgba(124, 58, 237, 0.42) 0%,
              rgba(139, 92, 246, 0.28) 30%,
              rgba(109, 40, 217, 0.14) 52%,
              rgba(76, 29, 149, 0.04) 72%,
              transparent 100%
            );
            transition:
              opacity 500ms cubic-bezier(.22, .8, .22, 1),
              transform 700ms cubic-bezier(.22, .8, .22, 1);
          }

          .avatar-2d-aura {
            position: absolute;
            border-radius: 50%;
            pointer-events: none;
            opacity: 0;
            filter: blur(18px);
            transition:
              opacity 500ms ease,
              transform 700ms ease;
          }

          .avatar-2d-aura-one {
            z-index: 6;
            width: clamp(315px, 46vh, 500px);
            height: clamp(315px, 46vh, 500px);
            max-width: 88%;
            max-height: 90%;
            background: conic-gradient(
              from 0deg,
              rgba(124, 58, 237, 0.04),
              rgba(168, 85, 247, 0.38),
              rgba(99, 102, 241, 0.08),
              rgba(139, 92, 246, 0.34),
              rgba(124, 58, 237, 0.04)
            );
          }

          .avatar-2d-aura-two {
            z-index: 5;
            width: clamp(350px, 54vh, 570px);
            height: clamp(350px, 54vh, 570px);
            max-width: 96%;
            max-height: 96%;
            background: radial-gradient(
              circle,
              transparent 45%,
              rgba(124, 58, 237, 0.18) 58%,
              rgba(168, 85, 247, 0.10) 70%,
              transparent 78%
            );
            filter: blur(12px);
          }

          .avatar-2d-speaking-ring {
            position: absolute;
            border-radius: 50%;
            pointer-events: none;
            z-index: 20;
            opacity: 0;
            border: 1px solid rgba(167, 139, 250, 0.55);
            box-shadow:
              0 0 22px rgba(124, 58, 237, 0.42),
              0 0 48px rgba(139, 92, 246, 0.22);
          }

          .avatar-2d-speaking-ring-one {
            width: clamp(300px, 42vh, 460px);
            height: clamp(300px, 42vh, 460px);
            max-width: 80%;
            max-height: 84%;
          }

          .avatar-2d-speaking-ring-two {
            width: clamp(335px, 48vh, 520px);
            height: clamp(335px, 48vh, 520px);
            max-width: 90%;
            max-height: 92%;
            border-color: rgba(139, 92, 246, 0.22);
            box-shadow: 0 0 34px rgba(124, 58, 237, 0.22);
          }

          /* AI speaking */
          .avatar-2d-wrapper.speaking .avatar-2d-frame {
            transform: scale(1.015);
            box-shadow:
              0 14px 42px rgba(0, 0, 0, 0.5),
              0 0 0 1px rgba(255, 255, 255, 0.22),
              0 0 30px rgba(124, 58, 237, 0.32);
          }

          .avatar-2d-wrapper.speaking .avatar-2d-ambient {
            opacity: 1;
            transform: scale(1);
            animation: avatar-2d-ambient-pulse 2.4s ease-in-out infinite;
          }

          .avatar-2d-wrapper.speaking .avatar-2d-aura-one {
            opacity: 1;
            animation: avatar-2d-aura-pulse 3.8s ease-in-out infinite;
          }

          .avatar-2d-wrapper.speaking .avatar-2d-aura-two {
            opacity: 1;
            animation: avatar-2d-aura-breathe 2.8s ease-in-out infinite;
          }

          .avatar-2d-wrapper.speaking .avatar-2d-speaking-ring-one {
            opacity: 0.95;
            animation: avatar-2d-ring-pulse 2.1s ease-in-out infinite;
          }

          .avatar-2d-wrapper.speaking .avatar-2d-speaking-ring-two {
            opacity: 0.7;
            animation: avatar-2d-ring-expand 2.8s ease-in-out infinite;
          }

          .avatar-2d-wrapper.speaking .avatar-2d-ring {
            border-color: rgba(167, 139, 250, 0.58);
            box-shadow:
              0 0 18px rgba(124, 58, 237, 0.38),
              inset 0 0 26px rgba(124, 58, 237, 0.12);
          }

          @keyframes avatar-2d-ambient-pulse {
            0%, 100% {
              opacity: 0.72;
              transform: scale(0.96);
            }
            50% {
              opacity: 1;
              transform: scale(1.06);
            }
          }

          @keyframes avatar-2d-aura-pulse {
            0%, 100% {
              transform: scale(0.92) rotate(0deg);
              opacity: 0.52;
            }
            50% {
              transform: scale(1.04) rotate(180deg);
              opacity: 0.9;
            }
          }

          @keyframes avatar-2d-aura-breathe {
            0%, 100% {
              transform: scale(0.96);
              opacity: 0.38;
            }
            50% {
              transform: scale(1.07);
              opacity: 0.7;
            }
          }

          @keyframes avatar-2d-ring-pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.55;
            }
            50% {
              transform: scale(1.035);
              opacity: 1;
            }
          }

          @keyframes avatar-2d-ring-expand {
            0% {
              transform: scale(0.98);
              opacity: 0.65;
            }
            70% {
              transform: scale(1.075);
              opacity: 0.18;
            }
            100% {
              transform: scale(1.1);
              opacity: 0;
            }
          }

          /* Listening = clean avatar, no animated glow */
          .avatar-2d-wrapper.listening .avatar-2d-ambient,
          .avatar-2d-wrapper.listening .avatar-2d-aura,
          .avatar-2d-wrapper.listening .avatar-2d-speaking-ring {
            opacity: 0;
            animation: none;
          }

          .avatar-2d-wrapper.listening .avatar-2d-frame {
            transform: scale(1);
          }

          @media (prefers-reduced-motion: reduce) {
            .avatar-2d-wrapper.speaking .avatar-2d-ambient,
            .avatar-2d-wrapper.speaking .avatar-2d-aura-one,
            .avatar-2d-wrapper.speaking .avatar-2d-aura-two,
            .avatar-2d-wrapper.speaking .avatar-2d-speaking-ring-one,
            .avatar-2d-wrapper.speaking .avatar-2d-speaking-ring-two {
              animation: none;
            }
          }

          @media (max-width: 768px) {
            .avatar-2d-frame {
              width: clamp(220px, 46vw, 320px);
              height: clamp(220px, 46vw, 320px);
              max-width: 68%;
              max-height: 72%;
            }

            .avatar-2d-ring {
              width: clamp(236px, 49vw, 336px);
              height: clamp(236px, 49vw, 336px);
            }

            .avatar-2d-ambient {
              width: clamp(270px, 62vw, 390px);
              height: clamp(270px, 62vw, 390px);
            }

            .avatar-2d-aura-one {
              width: clamp(260px, 58vw, 370px);
              height: clamp(260px, 58vw, 370px);
            }

            .avatar-2d-aura-two {
              width: clamp(290px, 68vw, 420px);
              height: clamp(290px, 68vw, 420px);
            }

            .avatar-2d-speaking-ring-one {
              width: clamp(248px, 52vw, 350px);
              height: clamp(248px, 52vw, 350px);
            }

            .avatar-2d-speaking-ring-two {
              width: clamp(275px, 60vw, 400px);
              height: clamp(275px, 60vw, 400px);
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default TwoDAvatar;